import { Controller } from '@hotwired/stimulus'

const STORAGE_PREFIX = 'cart_ticket_'

export default class extends Controller {
  static targets = [
    'row',
    'quantity',
    'maxHint',
    'total',
    'limitMessage',
    'submit',
  ]

  static values = {
    packMax: Number,
    currency: String,
    readyLabel: String,
    emptyLabel: String,
  }

  connect() {
    this._restore()
    this._refresh()
  }

  change(event) {
    const row = this._rowFor(event.target)
    if (!row) return
    this._setQuantity(row, this._quantityOf(row))
    this._refresh()
  }

  increment(event) {
    const row = this._rowFor(event.currentTarget)
    if (!row) return
    this._setQuantity(row, this._quantityOf(row) + 1)
    this._refresh()
  }

  decrement(event) {
    const row = this._rowFor(event.currentTarget)
    if (!row) return
    const next = this._quantityOf(row) - 1
    this._setQuantity(row, next < this._minOf(row) ? 0 : next)
    this._refresh()
  }

  submit() {
    this._activeRows().forEach((row) => this._forget(row))
  }

  _restore() {
    this._activeRows().forEach((row) => {
      if (this._quantityOf(row) > 0) {
        this._forget(row)
        return
      }

      const stored = this._read(row)
      if (stored !== null) this._setQuantity(row, parseInt(stored, 10))
    })
  }

  _refresh() {
    let total = 0
    let count = 0

    this._activeRows().forEach((row) => {
      const quantity = this._quantityOf(row)
      const price = parseFloat(row.dataset.price)
      count += quantity
      if (!isNaN(price)) total += quantity * price
    })

    const capped = this._capped(count)

    this._activeRows().forEach((row) => {
      const quantity = this._quantityOf(row)
      const max = this._maxOf(row)
      const atMax = max !== null && quantity >= max

      const hint = this._hintFor(row)
      if (hint) hint.classList.toggle('hidden', !atMax)

      const plus = row.querySelector(
        '[data-action*="event-ticket-picker#increment"]',
      )
      if (plus) plus.disabled = atMax || capped

      const minus = row.querySelector(
        '[data-action*="event-ticket-picker#decrement"]',
      )
      if (minus) minus.disabled = quantity <= 0
    })

    if (this.hasTotalTarget) {
      this.totalTarget.textContent = `${this.currencyValue}${total.toFixed(2)}`
    }
    if (this.hasLimitMessageTarget) {
      this.limitMessageTarget.classList.toggle('hidden', !capped)
    }
    if (this.hasSubmitTarget) {
      const empty = count <= 0
      this.submitTarget.disabled = empty
      this.submitTarget.textContent = empty
        ? this.emptyLabelValue
        : this.readyLabelValue
    }
  }

  _setQuantity(row, value) {
    const input = this._inputFor(row)
    if (!input) return

    const next = this._clamp(row, value)
    input.value = String(next)
    this._write(row, next)
  }

  _clamp(row, value) {
    let next = parseInt(value, 10)
    if (isNaN(next) || next <= 0) return 0

    const min = this._minOf(row)
    const max = this._maxOf(row)
    next = Math.max(next, min)
    if (max !== null && next > max) next = max

    const budget = this._budgetFor(row)
    if (next > budget) next = budget
    return next < min ? 0 : next
  }

  _activeRows() {
    if (!this.hasRowTarget) return []
    return this.rowTargets.filter((row) => this._inputFor(row))
  }

  _rowFor(element) {
    if (!element || !this.hasRowTarget) return null
    return this.rowTargets.find((row) => row.contains(element)) || null
  }

  _inputFor(row) {
    if (!this.hasQuantityTarget) return null
    return this.quantityTargets.find((input) => row.contains(input)) || null
  }

  _hintFor(row) {
    if (!this.hasMaxHintTarget) return null
    return this.maxHintTargets.find((hint) => row.contains(hint)) || null
  }

  _quantityOf(row) {
    const input = this._inputFor(row)
    if (!input) return 0
    const value = parseInt(input.value, 10)
    return isNaN(value) ? 0 : value
  }

  _minOf(row) {
    const min = parseInt(row.dataset.min, 10)
    return isNaN(min) ? 0 : min
  }

  _maxOf(row) {
    const max = parseInt(row.dataset.max, 10)
    return isNaN(max) ? null : max
  }

  _budgetFor(row) {
    if (!(this.packMaxValue > 0)) return Infinity
    const others = this._activeRows()
      .filter((other) => other !== row)
      .reduce((sum, other) => sum + this._quantityOf(other), 0)
    return Math.max(this.packMaxValue - others, 0)
  }

  _capped(count) {
    return this.packMaxValue > 0 && count >= this.packMaxValue
  }

  _key(row) {
    const id = row.dataset.ticketId
    return id ? `${STORAGE_PREFIX}${id}` : null
  }

  _read(row) {
    const key = this._key(row)
    if (!key) return null
    try {
      return sessionStorage.getItem(key)
    } catch {
      return null
    }
  }

  _write(row, value) {
    const key = this._key(row)
    if (!key) return
    try {
      sessionStorage.setItem(key, String(value))
    } catch {
      // sessionStorage can throw in private browsing
    }
  }

  _forget(row) {
    const key = this._key(row)
    if (!key) return
    try {
      sessionStorage.removeItem(key)
    } catch {
      // sessionStorage can throw in private browsing
    }
  }
}
